import React from 'react'
import PropTypes from 'prop-types'
import { withI18next } from 'lib/i18n/withI18next'
import { updateLagacyShoppingTrack } from 'lib/auth'
import { BASE_PRODUCT_LIMIT } from 'components/cart/const'
import { hasLocizeTranslation } from 'lib/utils/locize'
import SizeAndQuantitySelection from 'components/size-quantity-selection'
import CartChangeFreeGiftButton from '../cart-change-free-gift-button/index'

const CartProductSizeQuantity = ({
  allowProductUpdate,
  applyStoreCredit,
  cartProducts,
  isFreeGift,
  isSoldOut,
  openFreeGift,
  product,
  selectedAttribute,
  setError,
  updateCart,
  isShoppingBagNewDesign,
  t,
  i18n,
}) => {
  const hasMultipleFreeGifts =
    cartProducts?.eligibility?.free_gift?.has_multiple
  const isFreeGiftButtonDisplay =
    allowProductUpdate &&
    hasMultipleFreeGifts &&
    isFreeGift &&
    !isShoppingBagNewDesign

  const handleUpdateCart = async (
    productID,
    productToDeleteAttributeID,
    productToAddAttributeID,
    quantity,
  ) => {
    const updateCartRes = await updateCart({
      body: {
        is_store_credits_applied: applyStoreCredit,
      },
      productID,
      productToDeleteAttributeID,
      productToAddAttributeID,
      quantity,
      keepPayload: true,
      ignoreCache: true,
    })

    if (updateCartRes?.error || updateCartRes?.ok === false) {
      // The cart operation reconciles partial failures before returning. Never
      // blindly add the old variant: the server may already have changed it.
      setError(
        updateCartRes.error?.message ||
          updateCartRes.error ||
          hasLocizeTranslation(
            t,
            'CART_ITEM_UPDATE_UNCONFIRMED',
            i18n?.language?.split('-')[0] === 'th'
              ? 'ไม่สามารถยืนยันการเปลี่ยนสินค้าได้ กรุณารีเฟรชตะกร้าแล้วลองอีกครั้ง'
              : 'We couldn’t confirm the item change. Please refresh your bag and try again.',
          ),
      )
      return
    }

    const confirmedCart = updateCartRes?.cart || updateCartRes
    if (Array.isArray(confirmedCart?.items)) {
      await updateLagacyShoppingTrack(confirmedCart)
    }
  }

  const handleChangeQuantity = async (prodId, attrId, quantity = 1) => {
    const limit = product.max_quantity || BASE_PRODUCT_LIMIT
    await handleUpdateCart(prodId, attrId, attrId, Math.min(limit, quantity))
  }

  const handleChangeSize = async (val, attrIdOld, prodId) => {
    if (String(val) === String(attrIdOld)) return
    const target = product.attributes?.find(
      (attribute) => String(attribute.id_product_attribute) === String(val),
    )
    const existingTargetQuantity = (cartProducts?.items || []).reduce(
      (quantity, item) =>
        String(item.id_product) === String(prodId) &&
        item.attributes?.some(
          (attribute) =>
            attribute.selected &&
            String(attribute.id_product_attribute) === String(val),
        )
          ? quantity + item.quantity
          : quantity,
      0,
    )
    const destinationQuantity = product.quantity + existingTargetQuantity
    // Numeric stock is the available variant quantity, not the cart quantity.
    // Missing quantities are unknown and must still be checked by the backend.
    const available = target?.stock?.is_sold_out ? 0 : target?.stock?.quantity
    if (Number.isFinite(available) && destinationQuantity > available) {
      setError(
        hasLocizeTranslation(
          t,
          'CART_SIZE_QUANTITY_UNAVAILABLE',
          i18n?.language?.split('-')[0] === 'th'
            ? 'ไซซ์ %size% มีสินค้าเหลือ %quantity% ชิ้น กรุณาลดจำนวนหรือเลือกไซซ์อื่น'
            : 'Only %quantity% available in size %size%. Choose a lower quantity or another size.',
        )
          .replace('%quantity%', available)
          .replace('%size%', target.size),
      )
      return
    }
    const purchaseLimit = product.max_quantity || BASE_PRODUCT_LIMIT
    if (destinationQuantity > purchaseLimit) {
      setError(
        hasLocizeTranslation(
          t,
          'CART_SIZE_QUANTITY_PURCHASE_LIMIT',
          i18n?.language?.split('-')[0] === 'th'
            ? 'ซื้อสินค้าไซซ์ %size% ได้สูงสุด %quantity% ชิ้น กรุณาลดจำนวนหรือเลือกไซซ์อื่น'
            : 'You can add up to %quantity% in size %size%. Choose a lower quantity or another size.',
        )
          .replace('%quantity%', purchaseLimit)
          .replace('%size%', target?.size || ''),
      )
      return
    }
    // The existing add endpoint sets the destination line's total quantity.
    // Preserve any units already in that size as well as the units being moved.
    await handleUpdateCart(prodId, attrIdOld, val, destinationQuantity)
  }

  return (
    <React.Fragment>
      <SizeAndQuantitySelection
        allowProductUpdate={allowProductUpdate}
        handleChangeQuantity={handleChangeQuantity}
        handleChangeSize={handleChangeSize}
        isShoppingBagNewDesign={isShoppingBagNewDesign}
        isSoldOut={isSoldOut}
        product={product}
        selectedAttribute={selectedAttribute}
      />
      {isFreeGiftButtonDisplay && (
        <CartChangeFreeGiftButton onClick={openFreeGift} />
      )}
    </React.Fragment>
  )
}

CartProductSizeQuantity.defaultProps = {
  allowProductUpdate: undefined,
  applyStoreCredit: undefined,
  cartProducts: undefined,
  setError: undefined,
  updateCart: undefined,
  isShoppingBagNewDesign: false,
  i18n: undefined,
}

CartProductSizeQuantity.propTypes = {
  allowProductUpdate: PropTypes.bool,
  applyStoreCredit: PropTypes.bool,
  cartProducts: PropTypes.shape({
    eligibility: PropTypes.shape({
      free_gift: PropTypes.shape({ has_multiple: PropTypes.bool }),
    }),
    items: PropTypes.arrayOf(PropTypes.shape({})),
    summary: PropTypes.shape({ store_credits: PropTypes.shape({}) }),
  }),
  isFreeGift: PropTypes.bool.isRequired,
  isSoldOut: PropTypes.bool.isRequired,
  openFreeGift: PropTypes.func.isRequired,
  product: PropTypes.shape({
    id_product: PropTypes.number,
    attributes: PropTypes.arrayOf(PropTypes.shape({})),
    name: PropTypes.string,
    max_quantity: PropTypes.number,
    type: PropTypes.string,
    quantity: PropTypes.number,
  }).isRequired,
  selectedAttribute: PropTypes.shape({
    id_product_attribute: PropTypes.number,
    size: PropTypes.string,
    selected: PropTypes.bool,
    stock: PropTypes.shape({
      quantity: PropTypes.number,
      is_low_quantity: PropTypes.bool.isRequired,
      is_sold_out: PropTypes.bool.isRequired,
    }),
  }).isRequired,
  setError: PropTypes.func,
  updateCart: PropTypes.func,
  isShoppingBagNewDesign: PropTypes.bool,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({ language: PropTypes.string }),
}

export default withI18next()(CartProductSizeQuantity)
